import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListBackfillOpenBills,
  useUpdateBackfillMapping,
  useRunBackfillOpenBills,
  useListLedgerAccounts,
  useListCostCentres,
  getListBackfillOpenBillsQueryKey,
} from "@workspace/api-client-react";
import type { BackfillQueueRow } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { inr } from "@/lib/format";
import { useAuth } from "@/context/auth-context";
import { Info } from "lucide-react";

// A2 — `GET /ledger/backfill-open-bills` actually returns `{ items, summary
// }`; 57-09's generated OpenAPI contract only declares `BackfillQueueRow[]`
// (the same documented divergence 57-13/57-16 flag for ap-by-vendor and the
// run/generate-bills endpoints). Cast once, at the read boundary, rather
// than hand-writing a second fetch.
interface BackfillOpenBillsResponse {
  items: BackfillQueueRow[];
  summary: { total: number; ready: number; needsMapping: number };
}

interface BackfillRunResultReal {
  posted: number;
  skipped: number;
  needsMapping: number;
  quarantined: number;
  note: string;
  remedy: string | null;
}

export function BackfillTab() {
  const { toast } = useToast();
  const { user } = useAuth();
  const qc = useQueryClient();
  const isSuperAdmin = user?.role === "super_admin";

  const listQuery = useListBackfillOpenBills();
  const { data: accounts } = useListLedgerAccounts({ type: "expense", active: true });
  const { data: costCentres } = useListCostCentres();
  const updateMapping = useUpdateBackfillMapping();
  const runBackfill = useRunBackfillOpenBills();

  const [mappingDraft, setMappingDraft] = useState<Record<number, { expenseAccountId: number | null; costCentreId: number | null }>>({});
  const [lastNote, setLastNote] = useState<string | null>(null);

  const response = listQuery.data as unknown as BackfillOpenBillsResponse | undefined;
  const rows = response?.items ?? [];
  const summary = response?.summary ?? { total: 0, ready: 0, needsMapping: 0 };

  function refresh() {
    qc.invalidateQueries({ queryKey: getListBackfillOpenBillsQueryKey() });
  }

  function draftFor(bill: BackfillQueueRow) {
    return mappingDraft[bill.id] ?? { expenseAccountId: null, costCentreId: null };
  }

  async function saveMapping(bill: BackfillQueueRow) {
    const draft = draftFor(bill);
    if (draft.expenseAccountId == null || draft.costCentreId == null) {
      toast({ title: "Choose both an expense account and a cost centre.", variant: "destructive" });
      return;
    }
    try {
      await updateMapping.mutateAsync({
        id: bill.id,
        data: { lines: bill.lines.map((l) => ({ id: l.id, expenseAccountId: draft.expenseAccountId!, costCentreId: draft.costCentreId! })) },
      });
      refresh();
      toast({ title: `Bill #${bill.id}'s mapping corrected — ready for the next run.` });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not save mapping.", variant: "destructive" });
    }
  }

  async function handleRun() {
    try {
      const result = await runBackfill.mutateAsync();
      const real = result as unknown as BackfillRunResultReal;
      refresh();
      setLastNote(real.note);
      toast({
        title: `Posted ${real.posted}, needs mapping ${real.needsMapping}, quarantined ${real.quarantined}.`,
        description: real.remedy ?? undefined,
        variant: real.needsMapping > 0 || real.quarantined > 0 ? "destructive" : "default",
      });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not run backfill.", variant: "destructive" });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-4">
        <div className="text-sm">
          <span className="font-semibold">{summary.total}</span> open migrated bills ·{" "}
          <span className="font-semibold text-green-700">{summary.ready}</span> ready ·{" "}
          <span className="font-semibold text-destructive">{summary.needsMapping}</span> need mapping
        </div>
        <div className="flex-1" />
        {isSuperAdmin ? (
          <Button onClick={handleRun} disabled={summary.ready === 0 || runBackfill.isPending}>
            {runBackfill.isPending ? "Running…" : "Run backfill"}
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">Running the backfill requires the super_admin role.</span>
        )}
      </div>

      {lastNote && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>Scope of this run</AlertTitle>
          <AlertDescription>{lastNote}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bill #</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Bill date</TableHead>
                <TableHead className="text-right">Gross</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Mapping</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-20 text-center text-muted-foreground">
                    {listQuery.isLoading ? "Loading…" : "No open migrated bills awaiting posting."}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((bill) => (
                  <TableRow key={bill.id}>
                    <TableCell className="font-medium">Bill #{bill.id}</TableCell>
                    <TableCell className="text-sm">{bill.vendorName}</TableCell>
                    <TableCell className="text-sm">{bill.billDate.slice(0, 10)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{inr(bill.grossTotal)}</TableCell>
                    <TableCell>
                      {bill.needsMapping ? <Badge variant="destructive">Needs mapping</Badge> : <Badge variant="secondary">Ready</Badge>}
                    </TableCell>
                    <TableCell>
                      {bill.needsMapping ? (
                        <div className="flex items-center gap-1">
                          <Select
                            value={draftFor(bill).expenseAccountId != null ? String(draftFor(bill).expenseAccountId) : ""}
                            onValueChange={(v) =>
                              setMappingDraft((d) => ({ ...d, [bill.id]: { ...draftFor(bill), expenseAccountId: Number(v) } }))
                            }
                          >
                            <SelectTrigger className="w-[160px]">
                              <SelectValue placeholder="Expense account" />
                            </SelectTrigger>
                            <SelectContent>
                              {(accounts ?? []).map((a) => (
                                <SelectItem key={a.id} value={String(a.id)}>
                                  {a.code} · {a.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={draftFor(bill).costCentreId != null ? String(draftFor(bill).costCentreId) : ""}
                            onValueChange={(v) => setMappingDraft((d) => ({ ...d, [bill.id]: { ...draftFor(bill), costCentreId: Number(v) } }))}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue placeholder="Cost centre" />
                            </SelectTrigger>
                            <SelectContent>
                              {(costCentres ?? [])
                                .filter((c) => c.active)
                                .map((c) => (
                                  <SelectItem key={c.id} value={String(c.id)}>
                                    {c.code} · {c.name}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <Button size="sm" onClick={() => saveMapping(bill)} disabled={updateMapping.isPending}>
                            Save
                          </Button>
                          <span className="text-[11px] text-muted-foreground">map this schedule's expense account, then re-run.</span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {bill.lines[0]?.expenseAccountCode} · {bill.lines[0]?.costCentreName}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
